import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Tooltip,
  message,
  Modal,
  Button,
  Input,
  Radio,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getCommentDetails, replyToComment } from '@/services/xiaohongshu';

interface CommentDetailProps {
  name: string;
  sxtProcesses: { id: string; name: string }[];
}

interface CommentItem {
  id: string;
  noteId: string;
  noteTitle?: string;
  source: string;
  content: string;
  isReplied: boolean;
  replyMethod?: 'comment' | 'sxt';
  replyContent?: string;
  replyTime?: string;
  createdAt: string;
  xsecToken?: string;
}

const CommentDetail: React.FC<CommentDetailProps> = ({
  name,
  sxtProcesses,
}) => {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  // Modal相关状态
  const [replyModalVisible, setReplyModalVisible] = useState<boolean>(false);
  const [currentComment, setCurrentComment] = useState<CommentItem | null>(
    null,
  );
  const [replyContent, setReplyContent] = useState<string>('');
  const [replySubmitLoading, setReplySubmitLoading] = useState<boolean>(false);
  const [replyMethod, setReplyMethod] = useState<'comment' | 'sxt'>('comment');
  const [selectedListenerId, setSelectedListenerId] = useState<string>('');

  // 获取评论数据
  const fetchComments = async (page: number = 1, pageSize: number = 50) => {
    if (!name) return;

    setLoading(true);
    try {
      const response: any = await getCommentDetails({
        name,
        page,
        pageSize,
      });

      setComments(response.resp.data.data);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: response.resp.data.total,
      }));
    } catch (error) {
      message.error('获取评论详情失败');
      console.error('获取评论详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理表格分页变化
  const handleTableChange = (pagination: any) => {
    fetchComments(pagination.current, pagination.pageSize);
  };

  // 打开回复Modal
  const handleOpenReplyModal = (comment: CommentItem) => {
    setCurrentComment(comment);
    setReplyContent('');
    setReplyModalVisible(true);
  };

  // 关闭回复Modal
  const handleCloseReplyModal = () => {
    setReplyModalVisible(false);
    setCurrentComment(null);
    setReplyContent('');
    setReplyMethod('comment');
    setSelectedListenerId('');
  };

  // 提交回复
  const handleSubmitReply = async () => {
    if (!currentComment || !replyContent.trim()) {
      message.error('回复内容不能为空');
      return;
    }

    if (replyMethod === 'sxt' && !selectedListenerId) {
      message.error('使用SXT回复时，请选择监听器');
      return;
    }

    setReplySubmitLoading(true);
    try {
      await replyToComment({
        commentId: parseInt(currentComment.id),
        replyContent: replyContent.trim(),
        replyMethod: replyMethod,
        listenerId: replyMethod === 'sxt' ? selectedListenerId : undefined,
      });

      message.success('回复成功');
      handleCloseReplyModal();
      // 重新获取数据
      fetchComments(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('回复失败');
      console.error('回复失败:', error);
    } finally {
      setReplySubmitLoading(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    if (name) {
      fetchComments();
    }
  }, [name]);

  // 表格列定义
  const columns: ColumnsType<CommentItem> = [
    {
      title: '笔记',
      dataIndex: 'noteTitle',
      key: 'noteTitle',
      width: 150,
      ellipsis: true,
      render: (title: string, record: CommentItem) => {
        const noteUrl = `https://www.xiaohongshu.com/explore/${
          record.noteId
        }?channelType=web_engagement_notification_page&channelTabId=likes&xsec_token=${
          record.xsecToken || 'AB_5fYFT06oqWRqAaEVl3cfo2uZ7X-z71FNO15dCpMlwE='
        }&xsec_source=pc_notice`;

        return (
          <Tooltip title={`点击打开笔记: ${title || record.noteId}`}>
            <a
              href={noteUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#1890ff',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) =>
                ((e.target as HTMLElement).style.textDecoration = 'underline')
              }
              onMouseLeave={(e) =>
                ((e.target as HTMLElement).style.textDecoration = 'none')
              }
            >
              {title || `笔记 #${record.noteId}`}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      width: 300,
      ellipsis: true,
      render: (content: string) => (
        <Tooltip title={content}>
          <div
            style={{
              maxWidth: 300,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {content}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '已回复',
      dataIndex: 'isReplied',
      key: 'isReplied',
      width: 80,
      align: 'center',
      render: (isReplied: boolean) => (
        <Tag color={isReplied ? 'success' : 'default'}>
          {isReplied ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '回复方式',
      dataIndex: 'replyMethod',
      key: 'replyMethod',
      width: 100,
      align: 'center',
      render: (replyMethod: string | undefined, record: CommentItem) => {
        if (!record.isReplied || !replyMethod) return '-';
        return (
          <Tag color={replyMethod === 'comment' ? 'blue' : 'orange'}>
            {replyMethod === 'comment' ? '评论' : '私信'}
          </Tag>
        );
      },
    },
    {
      title: '回复内容',
      dataIndex: 'replyContent',
      key: 'replyContent',
      width: 200,
      align: 'center',
      render: (replyContent: string | undefined, record: CommentItem) => {
        if (record.isReplied && replyContent) {
          return (
            <Tooltip title={replyContent}>
              <div
                style={{
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {replyContent}
              </div>
            </Tooltip>
          );
        } else if (!record.isReplied) {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => handleOpenReplyModal(record)}
            >
              回复
            </Button>
          );
        } else {
          return '-';
        }
      },
    },
    {
      title: '回复时间',
      dataIndex: 'replyTime',
      key: 'replyTime',
      width: 150,
      sorter: (a: CommentItem, b: CommentItem) => {
        if (!a.replyTime && !b.replyTime) return 0;
        if (!a.replyTime) return 1;
        if (!b.replyTime) return -1;
        return (
          new Date(a.replyTime).getTime() - new Date(b.replyTime).getTime()
        );
      },
      sortDirections: ['descend', 'ascend'],
      render: (replyTime: string | undefined) => {
        if (!replyTime) return '-';
        return new Date(replyTime).toLocaleString('zh-CN');
      },
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={comments}
        loading={loading}
        rowKey="id"
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        size="small"
      />

      <Modal
        title="回复评论"
        open={replyModalVisible}
        onOk={handleSubmitReply}
        onCancel={() => setReplyModalVisible(false)}
        confirmLoading={replySubmitLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>原评论：</strong>
          <div
            style={{
              backgroundColor: '#f5f5f5',
              padding: '8px 12px',
              borderRadius: '4px',
              marginTop: '8px',
              fontSize: '14px',
            }}
          >
            {currentComment?.content}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <strong>回复方式：</strong>
          <Radio.Group
            value={replyMethod}
            onChange={(e) => {
              setReplyMethod(e.target.value);
              if (e.target.value === 'comment') {
                setSelectedListenerId('');
              }
            }}
            style={{ marginTop: '8px' }}
          >
            <Radio value="comment">评论回复</Radio>
            <Radio value="sxt">SXT回复</Radio>
          </Radio.Group>
        </div>

        {replyMethod === 'sxt' && (
          <div style={{ marginBottom: 16 }}>
            <strong>选择监听器：</strong>
            <Select
              value={selectedListenerId}
              onChange={setSelectedListenerId}
              placeholder="请选择SXT监听器"
              style={{ width: '100%', marginTop: '8px' }}
              options={sxtProcesses.map((process) => ({
                label: process.name,
                value: process.id,
              }))}
            />
          </div>
        )}

        <div>
          <strong>回复内容：</strong>
          <Input.TextArea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="请输入回复内容"
            rows={4}
            style={{ marginTop: '8px' }}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </div>
  );
};

export default CommentDetail;
